import React, { useEffect, useState } from 'react';
import { useTheme } from '../../styles/ThemeProvider';
import { FilterBar, DateRangePicker } from '../../components/analytics';
import { formatTRY, formatPct } from '../../utils/analytics/format';
import { getPresetRange, toIsoDate } from '../../utils/analytics/dateRange';
import { useFirmalar } from '../../hooks/useFirmalar';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const initialRange = () => {
  const r = getPresetRange('last30Days');
  return { preset: 'last30Days', start: toIsoDate(r.start), end: toIsoDate(r.end) };
};

export default function SatisKanalPage() {
  const { theme } = useTheme();
  const [range, setRange] = useState(initialRange);
  const [firma, setFirma] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { data: firmalar } = useFirmalar();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ baslangic: range.start, bitis: range.end });
        if (firma) params.append('firma', firma);
        const res = await fetch(`${API}/api/reports/satis-kanal?${params}`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [range.start, range.end, firma]);

  const kanalKodlari = data ? data.kanallar.map((k) => k.kod) : [];

  return (
    <div style={{ padding: theme.space.lg, display: 'flex', flexDirection: 'column', gap: theme.space.lg, background: theme.color.surfaceAlt, minHeight: '100vh' }}>
      <header>
        <h1 style={{ margin: 0, color: theme.color.text, fontSize: theme.font.size.xxl, fontWeight: theme.font.weight.bold }}>
          Satış Kanal Analizi
        </h1>
        <div style={{ color: theme.color.textSoft, fontSize: theme.font.size.sm, marginTop: 4 }}>
          POS ödeme kanallarının pay yüzdesi ve günlük dağılımı.
        </div>
      </header>

      <FilterBar>
        <DateRangePicker value={range} onChange={setRange} />
        <select
          value={firma}
          onChange={(e) => setFirma(e.target.value)}
          style={{
            padding: `${theme.space.xs}px ${theme.space.md}px`,
            borderRadius: theme.radius.sm,
            border: `1px solid ${theme.color.border}`,
            background: theme.color.surface,
            color: theme.color.text,
            fontSize: theme.font.size.xs,
            fontFamily: theme.font.family,
          }}
        >
          <option value="">Tüm Firmalar</option>
          {(firmalar || []).map((f) => <option key={f.kod} value={f.kod}>{f.ad}</option>)}
        </select>
      </FilterBar>

      {loading && <div style={{ padding: theme.space.md, color: theme.color.textSoft }}>Yükleniyor...</div>}
      {error && <div style={{ padding: theme.space.md, color: theme.color.danger, background: theme.color.dangerBg, borderRadius: theme.radius.sm }}>Hata: {error}</div>}

      {data && (
        <>
          {data.firma_eslesme_yok && (
            <section style={{ background: theme.color.surface, padding: theme.space.md, borderRadius: theme.radius.md, border: `1px solid ${theme.color.border}`, borderLeft: `4px solid ${theme.color.warning}`, color: theme.color.warning, fontSize: 13 }}>
              ⚠️ Seçili firmanın eşleşen aktif lokasyonu yok — sonuç boş gösteriliyor (tüm firmalara düşülmedi).
            </section>
          )}
          <section style={{ background: theme.color.surface, padding: theme.space.lg, borderRadius: theme.radius.md, border: `1px solid ${theme.color.border}` }}>
            <h3 style={{ marginTop: 0, color: theme.color.text }}>Kanal Pay Yüzdeleri (Toplam: {formatTRY(data.grand_total, { decimals: 0 })})</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.kanallar.map((k) => (
                <div key={k.kod}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 2 }}>
                    <span><strong>{k.ad}</strong></span>
                    <span>
                      <span style={{ fontFamily: 'monospace' }}>{formatTRY(k.toplam_tl, { decimals: 0 })}</span>
                      <span style={{ marginLeft: 8, color: theme.color.textSoft }}>{formatPct(k.pay_pct)}</span>
                    </span>
                  </div>
                  <div style={{ height: 14, background: theme.color.surfaceAlt, borderRadius: 4 }}>
                    <div style={{ width: `${k.pay_pct}%`, height: '100%', background: '#0d6efd', borderRadius: 4 }} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section style={{ background: theme.color.surface, padding: theme.space.lg, borderRadius: theme.radius.md, border: `1px solid ${theme.color.border}`, overflow: 'auto' }}>
            <h3 style={{ marginTop: 0, color: theme.color.text }}>Günlük Pivot</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'monospace' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.color.border}`, textAlign: 'right' }}>
                  <th style={{ padding: 6, textAlign: 'left' }}>Tarih</th>
                  {kanalKodlari.map((k) => (
                    <th key={k} style={{ padding: 6 }}>{k}</th>
                  ))}
                  <th style={{ padding: 6, fontWeight: 700 }}>Toplam</th>
                </tr>
              </thead>
              <tbody>
                {data.tarih_bazli.map((row) => (
                  <tr key={row.tarih} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                    <td style={{ padding: 6, textAlign: 'left' }}>{row.tarih}</td>
                    {kanalKodlari.map((k) => (
                      <td key={k} style={{ padding: 6, textAlign: 'right' }}>
                        {row.kanallar[k] ? formatTRY(row.kanallar[k], { decimals: 0 }).replace(' ₺', '') : '—'}
                      </td>
                    ))}
                    <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>
                      {formatTRY(row.toplam, { decimals: 0 }).replace(' ₺', '')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* BG-S1/BG-S4: görünür 'Diğer / Haritalanmamış' uyarısı. haritalanmamis
              alanı yeni backend'de gelir; eski backend'e karşı bilinmeyen_kanallar'a
              düşülür (deploy penceresi güvenli). */}
          {(() => {
            const hm = data.haritalanmamis;
            const kanallar = (hm && hm.kanallar) || data.bilinmeyen_kanallar || {};
            const adetVar = Object.keys(kanallar).length > 0;
            if (!adetVar) return null;
            const toplam = hm && typeof hm.toplam_tl === 'number'
              ? hm.toplam_tl
              : Object.values(kanallar).reduce((a, b) => a + b, 0);
            return (
              <section style={{ background: theme.color.surface, padding: theme.space.md, borderRadius: theme.radius.md, border: `1px solid ${theme.color.border}`, borderLeft: `4px solid ${theme.color.warning}` }}>
                <h4 style={{ marginTop: 0, color: theme.color.warning, fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span>⚠️ Diğer / Haritalanmamış Ödeme Kanalları</span>
                  <span style={{ fontFamily: 'monospace' }}>{formatTRY(toplam, { decimals: 2 })}</span>
                </h4>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: theme.color.text }}>
                  {Object.entries(kanallar).map(([tipi, tutar]) => (
                    <li key={tipi}>
                      <strong>{tipi}</strong>: {formatTRY(tutar, { decimals: 2 })}
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: 8, fontSize: 11, color: theme.color.textSoft }}>
                  {(hm && hm.mesaj)
                    || "Bu kanallar master'da eşleşmediği için kanal mix'ine ve komisyon hesabına girmez. Ayarlar › Komisyon Oranları'ndan ekleyin."}
                </div>
              </section>
            );
          })()}

          {data.duzeltmeler && Object.keys(data.duzeltmeler).length > 0 && (
            <section style={{ background: theme.color.surface, padding: theme.space.md, borderRadius: theme.radius.md, border: `1px solid ${theme.color.border}`, marginTop: theme.space.md }}>
              <h4 style={{ marginTop: 0, color: theme.color.textSoft, fontSize: 13 }}>Ciro Düzeltmeleri (ödeme kanalı değildir)</h4>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: theme.color.textSoft }}>
                {Object.entries(data.duzeltmeler).map(([tipi, tutar]) => (
                  <li key={tipi}>
                    <strong>{tipi}</strong>: {formatTRY(tutar, { decimals: 2 })}
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: 8, fontSize: 11, color: theme.color.textSoft }}>
                İndirim / ödenmez / ikram satırları net ciro hesabına girer; kanal kırılımına ve komisyona girmez (eski tarihli POS kayıtlarında görünür).
              </div>
            </section>
          )}

          {data.cari_detay && Object.keys(data.cari_detay).length > 0 && (
            <section style={{ background: theme.color.surface, padding: theme.space.md, borderRadius: theme.radius.md, border: `1px solid ${theme.color.border}`, marginTop: theme.space.md }}>
              <h4 style={{ marginTop: 0, color: theme.color.textSoft, fontSize: 13 }}>Cari Detayı — Kişi Kırılımı</h4>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: theme.color.textSoft }}>
                {Object.entries(data.cari_detay).map(([tipi, tutar]) => (
                  <li key={tipi}>
                    <strong>{tipi.replace(/^\*+/, '')}</strong>: {formatTRY(tutar, { decimals: 2 })}
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: 8, fontSize: 11, color: theme.color.textSoft }}>
                POS rulosunda CARİ satırının altındaki ***kişi kırılımıdır; tutar CARİ kaleminde zaten dahildir — toplamlara ikinci kez girmez.
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
